import { forwardRef } from 'react'

export type SwitchSize = 'sm' | 'md'

/** The background the switch sits on. */
export type SwitchSurface = 'dark' | 'light'

export interface SwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  size?: SwitchSize
  surface?: SwitchSurface
  id?: string
  'aria-label'?: string
  'data-testid'?: string
  className?: string
}

// Design-system Toggle tokens (Figma). Track/thumb dimensions and thumb travel
// distance (track width − 2×padding − thumb width) per size.
const SIZE_STYLES: Record<
  SwitchSize,
  { track: string; thumb: string; translate: string }
> = {
  sm: {
    track: 'h-[20px] w-[36px]',
    thumb: 'h-[16px] w-[16px]',
    translate: 'translate-x-[16px]',
  },
  md: {
    track: 'h-[24px] w-[44px]',
    thumb: 'h-[20px] w-[20px]',
    translate: 'translate-x-[20px]',
  },
}

// The off track (#87A8C6) is about 1.9:1 against a light surface such as the
// data table header (#DCE6EF), under the 3:1 WCAG asks of a control. On light
// surfaces a 1px border-secondary-inverse (#416D95, 4.3:1) outlines the track
// and takes a pixel of the padding, so the thumb keeps its size and travel.
const SURFACE_STYLES: Record<SwitchSurface, string> = {
  dark: 'border-0 p-[2px]',
  light: 'box-border border border-solid border-border-secondary-inverse p-px',
}

const getTrackBackground = (checked: boolean, disabled: boolean): string => {
  // Disabled keeps the live track colors and is muted via opacity on the button
  // (see below), so it reads as a faded version of the typical switch rather
  // than a stark white tag on the app's dark panels.
  if (disabled) {
    return checked ? 'bg-toggle-on' : 'bg-toggle-off'
  }
  return checked ? 'bg-toggle-on hover:bg-toggle-on-hover' : 'bg-toggle-off'
}

/**
 * Design-system Toggle.
 *
 * Pixel-matched to the Figma Toggle component (sizes, colors, focus ring,
 * thumb shadow). The thumb is positioned with a transform so the on/off
 * transition animates while landing on the spec's flex end/start positions.
 *
 * @example
 * ```tsx
 * <Switch checked={isEnabled} onChange={setIsEnabled} aria-label="Enable feature" />
 * ```
 */
const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked,
      onChange,
      disabled = false,
      size = 'sm',
      surface = 'dark',
      id,
      'aria-label': ariaLabel,
      'data-testid': dataTestId,
      className = '',
    },
    ref
  ) => {
    const handleClick = () => {
      if (!disabled) {
        onChange(!checked)
      }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleClick()
      }
    }

    const sizeStyles = SIZE_STYLES[size]

    return (
      <button
        ref={ref}
        id={id}
        type='button'
        role='switch'
        aria-checked={checked}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`
          inline-flex flex-row items-center rounded-full
          ${SURFACE_STYLES[surface]}
          transition-colors duration-200 ease-in-out
          focus:outline-hidden
          focus-visible:shadow-input-focus
          ${sizeStyles.track}
          ${getTrackBackground(checked, disabled)}
          ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
          ${className}
        `}
      >
        <span
          className={`
            inline-block rounded-full bg-white shadow-toggle-thumb
            transform transition-transform duration-200 ease-in-out
            ${sizeStyles.thumb}
            ${checked ? sizeStyles.translate : 'translate-x-0'}
          `}
        />
      </button>
    )
  }
)

Switch.displayName = 'Switch'

export default Switch
